import type { AppBoleta, AppBoletaProducto } from '../../types/planManejo';

// ─── Reporte: Productos Requeridos por Semana ───────────────────────────────────
// Consolida (suma) la cantidad a aplicar de cada producto en TODAS las boletas
// de una semana (o del rango visible), excluyendo canceladas. Pensado para que
// bodega / compras sepan cuánto de cada agroquímico se necesita esa semana.
// Sigue las reglas de reportes del ERP: HTML con CSS dedicado (no Tailwind),
// impresión vía iframe, y el mismo HTML se reutiliza para PDF (email/WhatsApp).

function fmtNPrint(n?: number | null, dec = 2) {
  if (n == null) return '';
  return n.toLocaleString('es-CR', { minimumFractionDigits: dec, maximumFractionDigits: dec });
}

function fmtFLong(s?: string | null) {
  if (!s) return '';
  const d = new Date(String(s).includes('T') ? s : `${s}T00:00:00`);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('es-CR', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function esc(v?: string | null) {
  return String(v ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

// Estados que NO entran en el consolidado (canceladas). Todo lo demás
// (pendiente, en_proceso, impresa, aplicada, archivada) sí suma.
const ESTADOS_EXCLUIDOS = new Set(['cancelada']);

// Aporte de una boleta a un producto (para el acordeón de auditoría y el Excel).
export interface AporteBoleta {
  numero: string;
  semana: string;
  lote_num: string;
  area_ha: number;
  cantidad: number;      // cuánto de ESTE producto aporta ESTA boleta
}

export interface ProductoConsolidado {
  articulo: string;
  nombre_producto: string;
  ingrediente: string;   // ingrediente activo / descripción de referencia
  medida: string;
  cantidad: number;      // Σ cantidad_aplicar
  boletas: number;       // en cuántas boletas aparece
  aportes: AporteBoleta[];  // desglose por boleta (solo vista/Excel, no PDF)
}

export interface ConsolidadoSemana {
  titulo: string;              // "Semana 22-26" o "Rango visible"
  semana: string;              // semana única, si aplica
  boletasIncluidas: number;    // # de boletas que aportaron
  areaTotal: number;           // Σ area_ha
  plantasTotal: number;        // Σ plantas
  aguaTotal: number;           // Σ agua requerida (litros)
  productos: ProductoConsolidado[];
}

// Contexto del filtro activo en la pantalla, para titular el reporte con lo que
// el usuario realmente está viendo (una semana, un rango de fechas, o el rango
// de semanas presentes). El reporte SIEMPRE consolida las boletas que se le
// pasan (las visibles); esto solo afecta el título.
export interface ContextoFiltro {
  semana?: string;                       // semana escrita en el buscador (ej. "22-26")
  fechaDesde?: string;                   // rango de fechas activo (si aplica)
  fechaHasta?: string;
}

// Normaliza la unidad de medida para que "Kilo"/"Kil"/"KIL" no dupliquen el
// mismo artículo. Se agrupa por artículo; la medida se muestra normalizada.
function normalizarMedida(m?: string | null): string {
  const raw = String(m ?? '').trim().toUpperCase();
  if (!raw) return '';
  if (raw.startsWith('KIL')) return 'KIL';   // Kilo, Kilos, Kil → KIL
  if (raw.startsWith('LIT') || raw === 'L' || raw === 'LT' || raw === 'LTS') return 'LIT';
  if (raw.startsWith('CAJ')) return 'CAJ';
  if (raw.startsWith('GR') || raw === 'G') return 'GR';
  if (raw.startsWith('UN') || raw.startsWith('UND')) return 'UN';
  return raw;
}

// Consolida las boletas + sus productos en un resumen por producto.
export function consolidarProductosSemana(
  boletas: AppBoleta[],
  productosPorBoleta: Map<number, AppBoletaProducto[]>,
  contexto?: ContextoFiltro,
): ConsolidadoSemana {
  const incluidas = boletas.filter(b => !ESTADOS_EXCLUIDOS.has(b.estado));
  const semanas = [...new Set(incluidas.map(b => String(b.semana ?? '').trim()).filter(Boolean))].sort();

  const mapa = new Map<string, ProductoConsolidado>();
  let areaTotal = 0;
  let plantasTotal = 0;
  let aguaTotal = 0;

  for (const b of incluidas) {
    areaTotal += Number(b.area_ha ?? 0);
    plantasTotal += Number(b.plantas ?? 0);
    const prods = productosPorBoleta.get(b.id) ?? [];
    for (const p of prods) {
      if ((p as any).excluida) continue;      // excluidos por el ingeniero no cuentan
      if (p.es_agua) {
        aguaTotal += Number(p.cantidad_aplicar ?? 0);
        continue;                            // se presenta separada de los productos
      }
      // Clave: código de artículo + medida NORMALIZADA (mismo producto no se
      // duplica por variantes de unidad "Kilo"/"Kil").
      const medidaNorm = normalizarMedida(p.medida);
      const key = `${String(p.articulo ?? '').trim()}||${medidaNorm}`;
      const curr = mapa.get(key) ?? {
        articulo: p.articulo ?? '',
        nombre_producto: p.nombre_producto ?? '',
        ingrediente: (p as any).ingrediente_activo || p.descripcion || '',
        medida: medidaNorm,
        cantidad: 0,
        boletas: 0,
        aportes: [] as AporteBoleta[],
      };
      const aporte = Number(p.cantidad_aplicar ?? 0);
      curr.cantidad += aporte;
      curr.boletas += 1;
      curr.aportes.push({
        numero: b.numero ?? '',
        semana: b.semana ?? '',
        lote_num: b.lote_num ?? '',
        area_ha: Number(b.area_ha ?? 0),
        cantidad: aporte,
      });
      mapa.set(key, curr);
    }
  }

  const productos = [...mapa.values()].sort((a, b) =>
    a.nombre_producto.localeCompare(b.nombre_producto, 'es'),
  );
  // Dentro de cada producto, ordenar los aportes por número de boleta (auditoría).
  for (const p of productos) {
    p.aportes.sort((a, b) => a.numero.localeCompare(b.numero, undefined, { numeric: true }));
  }

  // Título según el filtro activo: semana buscada > rango de fechas > semanas presentes.
  let titulo: string;
  if (contexto?.semana) {
    titulo = `Semana ${contexto.semana}`;
  } else if (contexto?.fechaDesde && contexto?.fechaHasta) {
    titulo = `${fmtFLong(contexto.fechaDesde)} al ${fmtFLong(contexto.fechaHasta)}`;
  } else if (semanas.length === 1) {
    titulo = `Semana ${semanas[0]}`;
  } else {
    titulo = 'Rango de semanas';
  }

  return {
    titulo,
    semana: semanas.length === 1 ? semanas[0] : semanas.join(', '),
    boletasIncluidas: incluidas.length,
    areaTotal,
    plantasTotal,
    aguaTotal,
    productos,
  };
}

// CSS dedicado del reporte (reglas ERP: no Tailwind, print-color-adjust exacto).
const REPORTE_CSS = `
  * { margin: 0; padding: 0; box-sizing: border-box; }
  body { font-family: Arial, Helvetica, sans-serif; font-size: 10pt; color: #000; background: #fff; }
  @page { size: Letter portrait; margin: 10mm 12mm 12mm; }
  .page { width: 100%; }

  .hdr { display: flex; align-items: flex-start; border-bottom: 2px solid #000; padding-bottom: 6px; margin-bottom: 8px; }
  .hdr-logo { width: 70px; margin-right: 10px; }
  .hdr-logo img { width: 65px; height: 65px; object-fit: contain; }
  .hdr-center { flex: 1; text-align: center; }
  .hdr-center .empresa { font-size: 14pt; font-weight: bold; color: #003399; }
  .hdr-center .cedula  { font-size: 8pt; color: #333; }
  .hdr-center .bpa     { font-size: 8pt; font-style: italic; color: #003399; font-weight: bold; margin-top: 2px; }
  .hdr-center .titulo-doc { font-size: 10pt; margin-top: 3px; font-weight: bold; }
  .hdr-right { width: 130px; border: 1.5px solid #000; text-align: center; padding: 4px; }
  .hdr-right .codigo { font-size: 12pt; font-weight: bold; color: #003399; }
  .hdr-right .cultivo { font-size: 7.5pt; }

  .semana-banda { text-align: center; font-size: 12pt; font-weight: bold; background: #003399; color: #fff; padding: 4px 0; margin-bottom: 6px; }

  .resumen { display: flex; gap: 10px; margin-bottom: 8px; flex-wrap: wrap; }
  .resumen .card { border: 1px solid #999; padding: 4px 10px; text-align: center; min-width: 90px; }
  .resumen .card .lbl { font-size: 7pt; text-transform: uppercase; letter-spacing: .06em; color: #555; }
  .resumen .card .val { font-size: 12pt; font-weight: bold; color: #003399; }

  table.prods { width: 100%; border-collapse: collapse; font-size: 9pt; }
  table.prods th { background: #dde; border: 1px solid #999; padding: 4px 5px; text-align: center; font-size: 8pt; }
  table.prods td { border: 1px solid #bbb; padding: 3px 5px; vertical-align: top; }
  table.prods tr:nth-child(even) td { background: #f6f8fc; }
  table.prods thead { display: table-header-group; }
  table.prods tr { break-inside: avoid; page-break-inside: avoid; }
  td.num    { text-align: center; width: 22px; color: #555; }
  td.left   { text-align: left; }
  td.right  { text-align: right; }
  td.center { text-align: center; }
  td.bold   { font-weight: bold; }
  td.small  { font-size: 8pt; color: #555; }
  table.prods tr.total-row td { background: #eef; border-top: 2px solid #000; font-weight: bold; font-size: 9.5pt; }
  table.prods tr.agua-row td { background: #e7f4ff; border-top: 2px solid #2878b5; font-weight: bold; }
  table.prods tbody.resumen-final { break-inside: avoid; page-break-inside: avoid; }

  .empty { padding: 20px; text-align: center; color: #777; font-style: italic; }
  .footer-bottom { display: flex; justify-content: space-between; font-size: 7pt; color: #555; border-top: 1px solid #ccc; padding-top: 4px; margin-top: 10px; }

  @media print {
    body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    .hdr, .semana-banda, .resumen, .footer-bottom { break-inside: avoid; page-break-inside: avoid; }
  }
`;

export function buildProductosSemanaHTML(
  consolidado: ConsolidadoSemana,
  empresaNombre: string,
  empresaCedula: string,
  empresaLogo = '',
): string {
  const { productos } = consolidado;

  const filas = productos.map((p, i) => `
    <tr>
      <td class="num">${i + 1}</td>
      <td class="left bold">${esc(p.articulo)}</td>
      <td class="left">${esc(p.nombre_producto)}</td>
      <td class="left small">${esc(p.ingrediente)}</td>
      <td class="center">${esc(p.medida)}</td>
      <td class="right bold">${fmtNPrint(p.cantidad, 2)}</td>
      <td class="center small">${p.boletas}</td>
    </tr>`).join('');

  const totalCantidad = productos.reduce((s, p) => s + p.cantidad, 0);

  const cuerpo = productos.length === 0
    ? `<tr><td colspan="7" class="empty">Sin productos requeridos en el rango seleccionado.</td></tr>`
    : filas;

  const resumenFinal = `
      <tr class="agua-row">
        <td></td>
        <td class="left bold">999</td>
        <td colspan="2" class="left">AGUA TOTAL A UTILIZAR</td>
        <td class="center">LIT</td>
        <td class="right">${fmtNPrint(consolidado.aguaTotal, 2)}</td>
        <td class="center">${consolidado.boletasIncluidas}</td>
      </tr>
      ${productos.length > 0 ? `
      <tr class="total-row">
        <td></td>
        <td colspan="4" class="left">TOTAL — ${productos.length} producto${productos.length === 1 ? '' : 's'}</td>
        <td class="right">${fmtNPrint(totalCantidad, 2)}</td>
        <td class="center">${consolidado.boletasIncluidas}</td>
      </tr>` : ''}`;

  return `<!DOCTYPE html>
<html lang="es">
<head>
<meta charset="UTF-8">
<title>Productos por semana — ${esc(consolidado.titulo)}</title>
<style>${REPORTE_CSS}</style>
</head>
<body>
<div class="page">

  <div class="hdr">
    <div class="hdr-logo">${empresaLogo ? `<img src="${esc(empresaLogo)}" alt="Logo">` : ''}</div>
    <div class="hdr-center">
      <div class="empresa">${esc(empresaNombre)}</div>
      <div class="cedula">Cédula Jurídica ${esc(empresaCedula)}</div>
      <div class="bpa">Manual de Buenas Prácticas Agrícolas</div>
      <div class="titulo-doc">Productos Requeridos por Semana — Consolidado</div>
    </div>
    <div class="hdr-right">
      <div class="codigo">RB03-AVZ</div>
      <div class="cultivo">Cultivo: Piña</div>
      <div class="cultivo">(Ananas Comosus)</div>
    </div>
  </div>

  <div class="semana-banda">${esc(consolidado.titulo)}${consolidado.semana && consolidado.titulo === 'Rango de semanas' ? ` (${esc(consolidado.semana)})` : ''}</div>

  <div class="resumen">
    <div class="card"><div class="lbl">Boletas</div><div class="val">${consolidado.boletasIncluidas}</div></div>
    <div class="card"><div class="lbl">Productos</div><div class="val">${productos.length}</div></div>
    <div class="card"><div class="lbl">Área</div><div class="val">${fmtNPrint(consolidado.areaTotal, 2)} ha</div></div>
    <div class="card"><div class="lbl">Plantas</div><div class="val">${consolidado.plantasTotal.toLocaleString('es-CR')}</div></div>
    <div class="card"><div class="lbl">Agua total</div><div class="val">${fmtNPrint(consolidado.aguaTotal, 2)} L</div></div>
  </div>

  <table class="prods">
    <thead>
      <tr>
        <th>#</th>
        <th>Artículo</th>
        <th>Nombre Comercial</th>
        <th>Fórmula / Ingrediente Activo</th>
        <th>Unidad</th>
        <th>Cantidad<br>Requerida</th>
        <th>Boletas</th>
      </tr>
    </thead>
    <tbody>${cuerpo}</tbody>
    <tbody class="resumen-final">${resumenFinal}</tbody>
  </table>

  <div class="footer-bottom">
    <span>Programa: Aplicación de Agroquímicos</span>
    <span>Consolidado de requerimientos — no sustituye la boleta individual</span>
    <span>Impreso: ${fmtFLong(new Date().toISOString().slice(0, 10))}</span>
  </div>

</div>
</body>
</html>`;
}

// ─── Export a Excel (CSV con BOM UTF-8, abre nativo en Excel con acentos) ──────
// Una fila por (producto × boleta) para auditar y pivotar. Cero dependencias.
function csvCell(value: unknown) {
  const s = String(value ?? '').replace(/"/g, '""');
  return `"${s}"`;
}

export function exportarProductosSemanaCSV(consolidado: ConsolidadoSemana) {
  const headers = ['Artículo', 'Producto', 'Ingrediente Activo', 'Unidad', 'Boleta', 'Semana', 'Lote', 'Área (ha)', 'Cantidad'];
  const lines: string[] = [headers.map(csvCell).join(',')];

  for (const p of consolidado.productos) {
    for (const a of p.aportes) {
      lines.push([
        p.articulo, p.nombre_producto, p.ingrediente, p.medida,
        a.numero, a.semana, a.lote_num,
        a.area_ha.toFixed(2), a.cantidad.toFixed(2),
      ].map(csvCell).join(','));
    }
  }
  lines.push([
    '999', 'AGUA TOTAL A UTILIZAR', '', 'LIT',
    '', consolidado.semana, '', consolidado.areaTotal.toFixed(2),
    consolidado.aguaTotal.toFixed(2),
  ].map(csvCell).join(','));

  // BOM (﻿) para que Excel lea UTF-8 y muestre acentos correctamente.
  const blob = new Blob(['﻿' + lines.join('\r\n')], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  const slug = String(consolidado.titulo || 'productos').replace(/[^\w-]+/g, '_');
  a.download = `productos-semana-${slug}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

// Imprime el reporte consolidado vía iframe oculto (mismo patrón que las boletas).
export function imprimirProductosSemana(
  consolidado: ConsolidadoSemana,
  empresaNombre: string,
  empresaCedula: string,
  empresaLogo = '',
) {
  const html = buildProductosSemanaHTML(consolidado, empresaNombre, empresaCedula, empresaLogo);
  const iframe = document.createElement('iframe');
  iframe.style.cssText = 'position:fixed;top:-9999px;left:-9999px;width:1px;height:1px;border:none;';
  document.body.appendChild(iframe);
  const doc = iframe.contentDocument || iframe.contentWindow?.document;
  if (!doc) { document.body.removeChild(iframe); return; }
  doc.open(); doc.write(html); doc.close();
  setTimeout(() => {
    iframe.contentWindow?.focus();
    iframe.contentWindow?.print();
    setTimeout(() => document.body.removeChild(iframe), 1000);
  }, 500);
}
